import { useMemo, useState } from "react";
import type { DeliveryClientPayload, DeliveryOrderRow } from "@mnp/shared";
import { ClientDrawer } from "../../clients/ClientDrawer.js";
import { ContactPopover } from "./ContactPopover.js";

interface ClientPanelProps {
  payload: DeliveryClientPayload | null;
  selectedIds: string[];
  onToggle: (orderId: string) => void;
  onSelectAll: () => void;
  onDeliver: () => void;
  onRefuse: () => void;
  onOpenOrder?: (order: { id: string; type: string }) => void;
  loading?: boolean;
}

function orderTitle(order: DeliveryOrderRow) {
  return `${order.type.toUpperCase()} · ${order.orderId.slice(0, 8)}`;
}

export function ClientPanel({
  payload,
  selectedIds,
  onToggle,
  onSelectAll,
  onDeliver,
  onRefuse,
  onOpenOrder,
  loading = false,
}: ClientPanelProps) {
  const [contactOpen, setContactOpen] = useState(false);
  const [clientDrawerOpen, setClientDrawerOpen] = useState(false);
  const selected = useMemo(
    () => payload?.orders.filter((order) => selectedIds.includes(order.orderId)) ?? [],
    [payload?.orders, selectedIds],
  );
  const selectedTotal = selected.reduce((sum, order) => sum + order.amountGel, 0);

  if (!payload) {
    return (
      <section className="flex min-h-96 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white text-gray-500">
        Отсканируйте QR клиента
      </section>
    );
  }

  return (
    <section className="relative flex min-h-96 flex-col rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{payload.client.name}</h2>
            <p className="text-sm text-gray-500">
              {payload.client.phone ?? "Телефон не указан"} · {payload.client.city ?? "Город не указан"}
            </p>
          </div>
          <div className="relative flex flex-wrap justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setClientDrawerOpen(true)}>
              Открыть карточку клиента
            </button>
            <button type="button" className="btn-secondary" onClick={() => setContactOpen((value) => !value)}>
              Связаться
            </button>
            {contactOpen && (
              <div className="absolute right-0 top-11 z-10 w-56">
                <ContactPopover client={payload.client} onClose={() => setContactOpen(false)} />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4 pb-24">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="text-sm font-medium text-gray-700">Заказы клиента</div>
          {payload.orders.length > 0 && (
            <button type="button" className="text-sm text-mnp-stamp hover:underline" onClick={onSelectAll}>
              Выбрать все
            </button>
          )}
        </div>
        {payload.orders.length === 0 ? (
          <div className="rounded border border-gray-200 p-4 text-sm text-gray-500">Заказов нет</div>
        ) : (
          <div className="grid gap-2">
            {payload.orders.map((order) => {
              // Already handed over / paid → locked: not selectable, a click
              // opens the order card instead of toggling a re-payment.
              if (order.paid) {
                const badge = order.type === "flex" ? "Оплачено" : "Выдано";
                return (
                  <button
                    type="button"
                    key={`${order.type}:${order.orderId}`}
                    onClick={() => onOpenOrder?.({ id: order.orderId, type: order.type })}
                    className="flex w-full items-center gap-3 rounded border border-gray-200 bg-gray-50 p-3 text-left hover:border-mnp-stamp"
                    title="Открыть заказ"
                    data-testid={`delivery-order-done-${order.orderId}`}
                  >
                    <span className="flex-1">
                      <span className="block text-sm font-medium text-gray-900">{orderTitle(order)}</span>
                      <span className="block text-xs text-gray-500">
                        {order.statusLabel}
                        <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700">
                          {badge}
                        </span>
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{order.amountGel.toFixed(2)} GEL</span>
                  </button>
                );
              }
              // BUG-02: terminal / not-yet-reviewed orders (shop cancelled/rejected/
              // pending/in_review, flex not-yet-final) cannot be handed over or paid.
              // Render them locked — no checkbox, no payable amount — so an operator
              // can't select one and 409 the whole batch.
              if (order.selectable === false) {
                return (
                  <button
                    type="button"
                    key={`${order.type}:${order.orderId}`}
                    onClick={() => onOpenOrder?.({ id: order.orderId, type: order.type })}
                    className="flex w-full items-center gap-3 rounded border border-gray-200 bg-gray-50 p-3 text-left hover:border-mnp-stamp"
                    title="Открыть заказ"
                    data-testid={`delivery-order-locked-${order.orderId}`}
                  >
                    <span className="flex-1">
                      <span className="block text-sm font-medium text-gray-900">{orderTitle(order)}</span>
                      <span className="block text-xs text-gray-500">
                        {order.statusLabel}
                        <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                          Недоступно к выдаче
                        </span>
                      </span>
                    </span>
                  </button>
                );
              }
              const checked = selectedIds.includes(order.orderId);
              return (
                <label
                  key={`${order.type}:${order.orderId}`}
                  className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${
                    checked ? "border-mnp-stamp bg-mnp-stamp/5" : "border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(order.orderId)}
                    aria-label={`Выбрать ${orderTitle(order)}`}
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-gray-900">{orderTitle(order)}</span>
                    <span className="block text-xs text-gray-500">{order.statusLabel}</span>
                  </span>
                  <span className="text-sm font-semibold text-gray-900">{order.amountGel.toFixed(2)} GEL</span>
                  <button
                    type="button"
                    className="text-sm text-mnp-stamp hover:underline"
                    onClick={(e) => {
                      // Don't toggle the checkbox the label wraps — just open the card.
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenOrder?.({ id: order.orderId, type: order.type });
                    }}
                    data-testid={`delivery-order-details-${order.orderId}`}
                  >
                    Подробнее
                  </button>
                </label>
              );
            })}
          </div>
        )}
      </div>
      <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-900">
          {selected.length} заказов · {selectedTotal.toFixed(2)} GEL
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onRefuse} disabled={selected.length === 0 || loading}>
            Отказ
          </button>
          <button type="button" className="btn-primary" onClick={onDeliver} disabled={selected.length === 0 || loading}>
            Выдать
          </button>
        </div>
      </div>
      {clientDrawerOpen && (
        <ClientDrawer clientId={payload.client.id} onClose={() => setClientDrawerOpen(false)} />
      )}
    </section>
  );
}
